import type { Currency, Language, Versioned } from './shared.types';
import type { PaymentMethodType } from './payment-types.types';

export interface EmailParams {
  bccEmailAddresses?: string[];
  ccEmailAddresses?: string[];
  toEmailAddresses: string[];
  bodyData: string;
  bodyCharset: EmailCharset;
  subjectdata: string;
  subjectCharset: EmailCharset;
  sourceEmail: string;
  replyToAddresses?: string[];
}

export enum EmailCharset {
  UTF8 = 'UTF-8',
  ISO88591 = 'ISO-8859-1', // Western European languages
  ISO885915 = 'ISO-8859-15', // Western European languages + Euro symbol
  USASCII = 'US-ASCII', // Basic English characters
  UTF16 = 'UTF-16', // Unicode characters (less commonly used)
  WINDOWS1252 = 'Windows-1252', // Microsoft Windows-specific charset
}

// ─── WhatsApp Webhook Event Persistence ───────────────────────────

export interface WhatsappWebhookEvent {
  /** Stable ULID generated at write time — use as a unique row key. */
  id?: string;
  phone_number_id: string;
  payload: WhatsappWebhookPayload;
  created_at: string;
  event_type: string;
}

export interface GetWhatsappEvents {
  phone_number_id?: string;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  count?: number;
}

export interface WhatsappEventsPage {
  events: WhatsappWebhookEvent[];
  count: number;
  cursor?: string;
}

/**
 * WhatsApp Business Platform webhook payload (Meta Cloud API).
 *
 * Deliberately permissive: only the envelope (`object`/`entry`/`changes`/`field`)
 * is validated, while each change's `value` is left open. This lets us ingest the
 * many field variants (messages, statuses, template_*, account_*) without rejecting
 * shapes we have not modelled yet. See docs:
 * https://developers.facebook.com/documentation/business-messaging/whatsapp/webhooks
 */
export interface WhatsappWebhookPayload {
  object: string;
  entry: WhatsappWebhookEntry[];
}

export interface WhatsappWebhookEntry {
  id: string;
  changes: WhatsappWebhookChange[];
}

export interface WhatsappWebhookChange {
  field: string;
  value: Record<string, unknown>;
}

// ─── IFTTT SMS Message Persistence ────────────────────────────────

/**
 * Body posted by the IFTTT "Make a web request" action when the Android
 * applet forwards a received SMS. The applet's body template is
 * user-configured, so no shape is guaranteed — anything JSON is accepted.
 */
export interface IftttSmsWebhookPayload {
  [key: string]: unknown;
}

/**
 * A raw SMS forwarded from the Android phone by IFTTT to the
 * `/notifications/sms/ifttt` webhook.
 *
 * `raw_body` always holds the request body exactly as received (the source
 * of truth); `sender`, `message_content` and `received_at` are best-effort
 * extractions from the well-known IFTTT body shapes.
 */
export interface IftttSmsMessage {
  /** Stable ULID generated at write time — use as a unique row key. */
  id?: string;
  /**
   * Queryable sender key: the originating number or alphanumeric SMS id
   * (e.g. "+258841234567", "M-Pesa"), falling back to the contact name and
   * then "unknown". Sender queries match this value.
   */
  sender: string;
  /** Contact name the phone resolved for the sender, when present. */
  sender_name?: string;
  /** SMS text extracted from the payload, when present. */
  message_content?: string;
  /** Receive timestamp reported by the phone/IFTTT, verbatim, when present. */
  received_at?: string;
  /** Name of the Android device that forwarded the SMS, when present. */
  device_name?: string;
  /** The raw request body exactly as received. */
  raw_body: string;
  /** Parsed JSON body, when the raw body is a JSON object. */
  payload?: Record<string, unknown>;
  /** Server-side ingestion timestamp (ISO-8601) — the query sort key. */
  created_at: string;
}

export interface GetIftttSmsMessages {
  sender?: string;
  start_date?: string;
  end_date?: string;
  cursor?: string;
  count?: number;
}

export interface IftttSmsMessagesPage {
  messages: IftttSmsMessage[];
  count: number;
  cursor?: string;
}

// ─── Notification Request (source services → service-notifications) ───

export enum NotificationRequestType {
  BUSINESS_SUMMARY = 'BUSINESS_SUMMARY',
  SHIFT_REPORT = 'SHIFT_REPORT',
  LOW_STOCK = 'LOW_STOCK',
  INVENTORY_COUNT_SUMMARY = 'INVENTORY_COUNT_SUMMARY',
  PRICE_CHANGES = 'PRICE_CHANGES',
  PURCHASE_ORDER_CHANGES = 'PURCHASE_ORDER_CHANGES',
  RECEIPT = 'RECEIPT',
  /** Covers both Debt Created and Debt Payment events. */
  DEBTS = 'DEBTS',
  DEBT_PAYMENT_REMINDER = 'DEBT_PAYMENT_REMINDER',
  EXPENSE = 'EXPENSE',
  AVAILABLE_MONEY = 'AVAILABLE_MONEY',
  INVOICE = 'INVOICE',
  TOPUP = 'TOPUP',
  /** Daily: purchase-order stock whose expiry date is inside its warning window. */
  EXPIRING_PRODUCTS = 'EXPIRING_PRODUCTS',
  /** A stock change service-inventory could not record (any caller). */
  INVENTORY_UPDATE_FAILED = 'INVENTORY_UPDATE_FAILED',
  /**
   * An urgent manual task (§4.13), sent to its assignee (or the members of
   * its role), never to subscribers. Off until a manager turns it on.
   */
  TASK_ASSIGNED = 'TASK_ASSIGNED',
}

export enum NotificationTrigger {
  EVENT = 'EVENT',
  SCHEDULED = 'SCHEDULED',
}

/**
 * Payload contract published by source services to the
 * `notification-requested` SNS topic (Phase 6 emitters).
 *
 * - `event_id` is required for EVENT-triggered notifications
 *   (dedupe key `EVENT#<eventId>`).
 * - `period_key` is required for SCHEDULED notifications
 *   (dedupe key `<type>#<periodKey>#<storeId>`, e.g. `LOW_STOCK#W2026-33#store-1`).
 * - `payload` carries the per-type render payload (Phase 3 renders it).
 */
export interface NotificationRequest {
  merchant_id: string;
  store_id: string;
  type: NotificationRequestType;
  trigger: NotificationTrigger;
  event_id?: string;
  period_key?: string;
  payload: Record<string, unknown>;
  /**
   * Only for `assignee_only` types (TASK_ASSIGNED): the employees to send
   * to, instead of the type's subscribers.
   */
  recipient_employee_ids?: string[];
}

export enum NotificationKind {
  EMPLOYEE = 'EMPLOYEE',
  CUSTOMER = 'CUSTOMER',
  INDEPENDENT = 'INDEPENDENT',
}

export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  READ = 'READ',
  FAILED = 'FAILED',
}

/**
 * One logical notification (notifications.md §7): one `MSG-` ID shared by
 * all recipient copies.
 */
export interface Notification {
  message_id: string;
  type: NotificationRequestType;
  trigger: NotificationTrigger;
  event_id?: string;
  period_key?: string;
  language: Language;
  merchant_id: string;
  store_id: string;
  payload: Record<string, unknown>;
  /** Rendered report media (set by the send worker after the first render). */
  media?: NotificationMedia;
  created_at: string;
  updated_at: string;
}

export interface NotificationDBItem extends Notification {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

/** One recipient copy of a notification. */
export interface NotificationRecipient {
  message_id: string;
  /** ULID identifying this recipient copy (stable across retries). */
  recipient_id: string;
  kind: NotificationKind;
  entity_id: string;
  phone_number: string;
  status: NotificationStatus;
  /** WhatsApp message id (wamid) of the sent message (Phase 3). */
  whatsapp_message_id?: string;
  /** Set when Meta reports the number invalid (error 131026) — never retry. */
  wa_id_invalid?: boolean;
  created_at: string;
}

export interface NotificationRecipientDBItem extends NotificationRecipient {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

/**
 * Dedupe marker (FR-13). Claimed with a conditional PutItem before any
 * other work so retries can never duplicate a notification.
 */
export interface NotificationDedupeMarker {
  dedupe_key: string;
  created_at: string;
}

export interface NotificationDedupeMarkerDBItem
  extends NotificationDedupeMarker {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /** Epoch-seconds TTL so stale markers expire (requires table TTL). */
  ttl?: number;
  /**
   * ISO time until which a leased claim is held; once past, another
   * delivery may take the key over. Missing = the claim is final (every
   * marker written without a lease).
   */
  lease_until?: string;
}

/** Per-store × type subscription parent record. */
export interface NotificationSubscription extends Versioned {
  store_id: string;
  type: NotificationRequestType;
  /** Whether this type is enabled for the store (defaults to true). */
  enabled?: boolean;
  /** Omitted means dynamically use the merchant-country default. */
  language?: Language;
  created_at: string;
  updated_at: string;
}

export interface NotificationSubscriptionDBItem
  extends NotificationSubscription {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}
/**
 * Subscription membership row — one per (store, type, user) recording the
 * date the user subscribed. (User decision 2026-08-25: subscriptions are
 * their own entity rows, no `subscriber_ids` array on the parent.)
 */
export interface NotificationSubscriber {
  store_id: string;
  type: NotificationRequestType;
  user_id: string;
  /** Defaults to EMPLOYEE for rows created before independent contacts. */
  kind?: NotificationKind.EMPLOYEE | NotificationKind.INDEPENDENT;
  subscribed_at: string;
}

export interface NotificationSubscriberDBItem extends NotificationSubscriber {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

/**
 * Internal send-task message published by persist to the notification send
 * queue — one message per recipient so delivery status is tracked per
 * recipient (Phase 3 worker consumes this).
 */
export interface SendNotificationTask {
  message_id: string;
  recipient_id: string;
  kind: NotificationKind;
  entity_id: string;
  phone_number: string;
  type: NotificationRequestType;
  language: Language;
  merchant_id: string;
  store_id: string;
  payload: Record<string, unknown>;
  /** Merchant currency used to format money in the rendered report. */
  currency?: Currency;
  /** Store IANA time zone used to format dates in the rendered report. */
  timezone?: string;
  notification_created_at: string;
}

// ─── Notification media (rendered report stored in S3 — FR-14) ───

export interface NotificationMedia {
  jpeg_url: string;
  pdf_url: string;
  jpeg_key: string;
  pdf_key: string;
}

// ─── Schedules (notifications.md §9 / FR-08, FR-09) ───

export enum NotificationScheduleFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

/** Minutes since store-local midnight; must be a multiple of 30 (FR-08). */
export const NOTIFICATION_SCHEDULE_TIME_STEP_MIN = 30;
export const NOTIFICATION_SCHEDULE_TIME_MAX_MIN = 23 * 60 + 30;
/** `month_day` value meaning "last day of the month" (FR-09 clamp). */
export const NOTIFICATION_SCHEDULE_LAST_DAY_OF_MONTH = 31;

/**
 * Manager-selected schedule for one store × type (item `NOTIFICATION_SCHEDULE`
 * in the POS main table). One EventBridge Scheduler entry per record fires
 * daily at `time_min` in `timezone`; the generator applies the weekly /
 * monthly guard (`isScheduledToday`).
 */
export interface NotificationSchedule extends Versioned {
  store_id: string;
  type: NotificationRequestType;
  frequency: NotificationScheduleFrequency;
  /** Store-local minutes since midnight in 30-minute steps (0 … 1410). */
  time_min: number;
  /** 0 = Sunday … 6 = Saturday. Required when `frequency` is WEEKLY. */
  weekday?: number;
  /** 1 … 31; 31 means "last day of the month". Required when MONTHLY. */
  month_day?: number;
  /** IANA time zone the schedule is evaluated in (store-local). */
  timezone: string;
  enabled: boolean;
  /** Name of the EventBridge Scheduler schedule once synced. */
  schedule_name?: string;
  /** ISO timestamp of the last generator run that emitted a request. */
  last_run_at?: string;
  /** periodKey of the last emitted request (e.g. `D2026-08-08`). */
  last_period_key?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationScheduleDBItem extends NotificationSchedule {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

/** Schedule fields a manager can set through the settings API. */
export interface NotificationScheduleInput {
  frequency: NotificationScheduleFrequency;
  time_min: number;
  weekday?: number;
  month_day?: number;
}

// ─── Settings API (notifications.md §11) ───

/** `PUT /notifications/settings/{merchant_id}/{store_id}/{type}` body. */
export interface UpdateNotificationSettings {
  enabled: boolean;
  /** Employee ids that receive this type (FR-02 / FR-07). */
  subscriber_ids: string[];
  /** Independent notification-contact ids. */
  contact_ids?: string[];
  /** null/omitted keeps the merchant-country default dynamic. */
  language?: Language | null;
  /**
   * Schedule for scheduled-capable types. `null`/omitted removes the
   * schedule (for PRICE_CHANGES this means "as it happens").
   */
  schedule?: NotificationScheduleInput | null;
}

export interface NotificationTypeSettings {
  type: NotificationRequestType;
  enabled: boolean;
  /** null means the merchant-country default is used dynamically. */
  language?: Language | null;
  subscribers: NotificationSubscriber[];
  /** Whether this type supports a D/W/M schedule. */
  schedulable: boolean;
  /** Whether this type also fires on domain events. */
  event_driven: boolean;
  schedule?: NotificationSchedule | null;
  /** ISO timestamps (UTC) of the next runs of the schedule, if any. */
  next_runs: string[];
}

export interface NotificationSettingsResponse {
  store_id: string;
  timezone: string;
  billing: NotificationBillingStatus;
  types: NotificationTypeSettings[];
}

// â”€â”€â”€ Merchant-wide independent notification contacts â”€â”€â”€

export interface NotificationContact extends Versioned {
  id: string;
  merchant_id: string;
  name: string;
  /** Normalized E.164 number; unique within a merchant. */
  phone_number: string;
  is_number_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationContactDBItem extends NotificationContact {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

export interface NotificationContactsResponse {
  contacts: NotificationContact[];
}

export interface CreateNotificationContact {
  name: string;
  phone_number: string;
}

export interface UpdateNotificationContact {
  name: string;
  phone_number: string;
}

export interface SendNotificationContactVerificationResponse {
  expires_at: string;
}

export interface VerifyNotificationContact {
  code: string;
}

export interface NotificationContactVerification {
  merchant_id: string;
  contact_id: string;
  /**
   * Identifies this challenge: attempts and cleanup apply only to it, never
   * to a code sent after it. Optional for backwards compatibility; a
   * challenge without it asks for a new code.
   */
  challenge_id?: string;
  /**
   * The number the code was sent to: only that number can be verified with
   * it. Optional for backwards compatibility; a challenge without it asks
   * for a new code.
   */
  phone_number?: string;
  code_hash: string;
  expires_at: string;
  attempts: number;
  created_at: string;
  /** DynamoDB TTL in epoch seconds. */
  ttl: number;
}

export interface NotificationBillingStatus {
  blocked: boolean;
  reason?: 'OVERDUE_NOTIFICATION_INVOICE';
  overdue_invoice_id?: string;
  overdue_invoice_number?: string;
  due_date?: string;
}

export interface NotificationUsage {
  merchant_id: string;
  store_id: string;
  /** Calendar month in UTC, YYYY-MM. */
  month: string;
  activated_at: string;
}

export interface NotificationUsageDBItem extends NotificationUsage {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
}

// ─── History API (notifications.md §11) ───

export interface GetNotificationsHistory {
  merchant_id: string;
  store_id: string;
  type?: NotificationRequestType;
  /** ISO date-time lower bound (inclusive) on `created_at`. */
  from?: string;
  /** ISO date-time upper bound (inclusive) on `created_at`. */
  to?: string;
  cursor?: string;
  count?: number;
}

export interface NotificationWithRecipients extends Notification {
  recipients: NotificationRecipient[];
}

export interface NotificationsHistoryPage {
  notifications: NotificationWithRecipients[];
  count: number;
  cursor?: string;
}

/** Manual resend request for one PENDING recipient copy. */
export interface ResendNotificationRecipient {
  merchant_id: string;
  store_id: string;
  message_id: string;
  recipient_id: string;
}

export interface ResendNotificationRecipientResponse {
  message: string;
  message_id: string;
  recipient_id: string;
}

// ─── Scheduler → generator invocation payload ───

/** Input the EventBridge Scheduler entry passes to the generator Lambdas. */
export interface ScheduledNotificationInvocation {
  merchant_id: string;
  store_id: string;
  type: NotificationRequestType;
}

// ─── Per-type render payloads (notifications.md §13) ───
//
// Source services publish these as `NotificationRequest.payload`; the send
// worker's layout builders read them. Money values are plain numbers in the
// merchant currency (the send worker formats them); dates are ISO strings.

export interface NotificationMoneyLine {
  name: string;
  amount: number;
}

export interface NotificationPaymentMethodBreakdown {
  name: string;
  sales: number;
  refunds: number;
  net: number;
}

export interface BusinessSummaryPayload {
  /** Human label, e.g. `8 Aug 2026 00:00 - 23:59`. */
  period_label: string;
  period_start: string;
  period_end: string;
  gross_sales: number;
  refunds: number;
  discounts: number;
  net_sales: number;
  cost_of_goods: number;
  gross_profit: number;
  expenses: number;
  net_profit: number;
  by_payment_method: NotificationPaymentMethodBreakdown[];
  by_employee: NotificationMoneyLine[];
}

export interface ShiftCountLine {
  name: string;
  payment_type: PaymentMethodType;
  expected: number;
  counted: number;
  difference: number;
}

export interface ShiftReportPayload {
  shift_id: string;
  opened_by: string;
  opened_at: string;
  closed_by: string;
  closed_at: string;
  duration_minutes: number;
  gross_sales: number;
  refunds: number;
  discounts: number;
  net_sales: number;
  /** Net sales per payment method. */
  payment_methods: NotificationMoneyLine[];
  /** Expected vs counted per payment method. */
  counts: ShiftCountLine[];
  total_difference: number;
  closing_note?: string;
}

export interface LowStockItem {
  product_id: string;
  name: string;
  in_stock: number;
  threshold: number;
}

export interface LowStockPayload {
  period_label: string;
  period_start: string;
  period_end: string;
  items: LowStockItem[];
  total_below_threshold: number;
}

export interface ExpiringProductItem {
  product_id: string;
  name: string;
  po_id: string;
  /** YYYY-MM-DD */
  expiry_date: string;
  /** Whole days until the expiry date; negative = already past. */
  days_left: number;
  /** Quantity received on the purchase-order line. */
  quantity: number;
  in_stock: number;
}

export interface ExpiringProductsPayload {
  period_label: string;
  period_start: string;
  period_end: string;
  items: ExpiringProductItem[];
  total_expiring: number;
}

export interface InventoryCountDifferenceItem {
  product_id: string;
  name: string;
  /** Signed quantity change in base units. */
  quantity_change: number;
  /** Optional human breakdown, e.g. `-504 × Caixa=10 + 1 × Unidade`. */
  quantity_label?: string;
  /** Signed inventory value change (cost). */
  value_change: number;
}

export interface InventoryCountSummaryPayload {
  period_label: string;
  period_start: string;
  period_end: string;
  report_date: string;
  counted_by: string[];
  items_with_changes: number;
  total_difference_value: number;
  largest_negative: InventoryCountDifferenceItem[];
  largest_positive: InventoryCountDifferenceItem[];
  net_difference_value: number;
}

export interface PriceChangeItem {
  product_id: string;
  name: string;
  old_price: number;
  new_price: number;
  changed_by?: string;
  changed_at?: string;
}

export interface PriceChangesPayload {
  /**
   * Period label of a scheduled digest. Omitted for event notifications,
   * which are dated by the first change's `changed_at` at render time.
   */
  date_label?: string;
  changed_by?: string;
  changes: PriceChangeItem[];
}

export enum PurchaseOrderNotificationEvent {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
}

export interface PurchaseOrderNotificationItem {
  name: string;
  quantity: number;
  cost: number;
  total: number;
}

export interface PurchaseOrderChangesPayload {
  po_id: string;
  event: PurchaseOrderNotificationEvent;
  ordered_by: string;
  supplier_name: string;
  purchase_date: string;
  items: PurchaseOrderNotificationItem[];
  total_cost: number;
  paid: number;
  amount_due: number;
  status: string;
  note?: string;
}

export interface ReceiptNotificationDiscount {
  name: string;
  amount: number;
}

export interface ReceiptNotificationLineItem {
  name: string;
  quantity: number;
  price: number;
  total: number;
  is_sold_by_weight?: boolean;
  note?: string;
  discounts?: ReceiptNotificationDiscount[];
}

export interface ReceiptNotificationTax {
  name: string;
  amount: number;
  type: 'INCLUDED' | 'ADDED';
}

export interface ReceiptNotificationPayment {
  name: string;
  payment_type: PaymentMethodType;
  amount: number;
  total_given?: number;
  change?: number;
}

export interface ReceiptNotificationDebt {
  starting: number;
  refunded: number;
  borrowed: number;
  ending: number;
  due_date?: string;
}

export interface ReceiptNotificationPoints {
  earned: number;
  deducted: number;
  balance: number;
}

/** Mirrors the printed classic receipt (native_app_pos ReceiptFormatter). */
export interface ReceiptPayload {
  receipt_number: string;
  custom_receipt_number: string;
  receipt_type: 'SALE' | 'REFUND';
  created_at: string;
  /** Verified customer copy is resolved from this id (FR-03). */
  customer_id?: string;
  customer_name?: string;
  employee_name: string;
  pos_device_name: string;
  refund_for?: string;
  associated_refunds?: string[];
  line_items: ReceiptNotificationLineItem[];
  subtotal: number;
  total_discount: number;
  tip: number;
  surcharge: number;
  total: number;
  taxes: ReceiptNotificationTax[];
  payments: ReceiptNotificationPayment[];
  debt?: ReceiptNotificationDebt;
  points?: ReceiptNotificationPoints;
}

export enum DebtNotificationEvent {
  DEBT_CREATED = 'DEBT_CREATED',
  DEBT_PAYMENT = 'DEBT_PAYMENT',
}

export interface DebtNotificationItem {
  name: string;
  quantity: number;
  price: number;
  total: number;
}

export interface DebtsPayload {
  event: DebtNotificationEvent;
  debt_id: string;
  /**
   * Debtor customer id — the verified customer copy is resolved from it
   * (FR-04). Omitted when the debtor is not a customer (e.g. an employee).
   */
  customer_id?: string;
  customer_name: string;
  employee_name: string;
  receipt_number?: string;
  date: string;
  /** DEBT_CREATED only. */
  items?: DebtNotificationItem[];
  purchase_total?: number;
  paid_today?: number;
  new_debt?: number;
  /** DEBT_PAYMENT only. */
  payment_amount?: number;
  payment_method?: string;
  previous_balance: number;
  total_outstanding: number;
  due_date?: string;
}

export enum DebtReminderTier {
  /** Sent once at T−3 days before the due date (FR-16). */
  DUE_SOON = 'DUE_SOON',
  /** Sent once per ISO week while unpaid and past due (FR-16). */
  OVERDUE = 'OVERDUE',
}

export interface DebtPaymentReminderPayload {
  tier: DebtReminderTier;
  debt_id: string;
  /** Debtor customer id; omitted when the debtor is not a customer. */
  customer_id?: string;
  customer_name: string;
  due_date: string;
  days_remaining?: number;
  days_overdue?: number;
  outstanding_balance: number;
  supplier_contact?: string;
}

export interface ExpensePayload {
  transaction_id: string;
  recorded_by: string;
  date: string;
  shift_id?: string;
  amount: number;
  paid_from: string;
  comments?: string;
}

/**
 * A stock change `POST /inventory/{m}/stores/{s}/products/{p}` could not
 * record: who made it, when, what and why it failed.
 */
export interface InventoryUpdateFailedPayload {
  product_id: string;
  product_name: string;
  /** `InventoryChangeReason`: Sale, Purchase Order, Inventory Count… */
  reason: string;
  /** Counts: the quantity counted; other changes: the change (+/−). */
  quantity: number;
  /** Who made the change (the cashier, the receiver, the counter). */
  actor_name: string;
  /** When the change happened at its source. */
  occurred_at: string;
  failed_at: string;
  /** What caused it, e.g. `Receipt 1-1042` or the purchase order. */
  source?: string;
  /** The server's reason. */
  error: string;
  status_code?: number;
}

export interface AvailableMoneyAccount {
  owner_name: string;
  balances: NotificationMoneyLine[];
}

export interface AvailableMoneyPayload {
  snapshot_at: string;
  snapshot_label: string;
  accounts: AvailableMoneyAccount[];
  totals: NotificationMoneyLine[];
}

export interface InvoicePayload {
  invoice_id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  due_date: string;
  subscription_fee: number;
  discounts: number;
  total: number;
  amount_due: number;
  status: string;
  currency?: string;
  /**
   * What the store must top up so its wallet covers everything it owes.
   * Optional for backwards compatibility; missing or 0 = nothing to top up.
   */
  top_up_amount?: number;
}

export interface TaskAssignedPayload {
  task_id: string;
  title: string;
  /** Written description, if any (an audio note is only mentioned). */
  description?: string;
  has_audio?: boolean;
  attachment_count?: number;
  due_at?: string;
  created_by: string;
  /** The person, or the role, it is assigned to. */
  assigned_to: string;
  created_at: string;
}

export interface TopupPayload {
  transaction_id: string;
  operator: string;
  sender: string;
  receiver_number: string;
  amount: number;
  status: string;
  claimed_by?: string;
  wallet_credited?: string;
  date: string;
}
